
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';

export const metadata = {
  toc: true,
  layout: 'guide',
  section: 'Payments',
  title: 'Payments Dashboard',
  description: 'Monitor payment activity, manage virtual accounts, and generate reports'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Callout = makeShortcode("Callout");
const Image = makeShortcode("Image");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Header title={metadata.title} subtitle={metadata.description} mdxType="Header" />
    <p>{`The Payments Dashboard provides a centralized interface for monitoring payment activity, managing virtual accounts, and generating reconciliation reports. Access the dashboard at `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com"
      }}>{`dashboard.plaid.com`}</a>{`.`}</p>
    <Callout mdxType="Callout">
      <p>{`A link to the Payments Dashboard appears in the Dashboard navigation after you have applied for access to `}<a parentName="p" {...{
          "href": "https://dashboard.plaid.com/overview/production"
        }}>{`Payments in the Production environment`}</a>{`.`}</p>
    </Callout>
    <h4 {...{
      "id": "overview"
    }}>{`Overview`}</h4>
    <p>{`The Payments Dashboard consists of two main sections for operational management:`}</p>
    <h5 {...{
      "id": "activity"
    }}>{`Activity`}</h5>
    <p>{`The Activity tab displays all payments created with Plaid, including one-time payments, Variable Recurring Payments (VRPs), refunds, payouts, and sweeps. Use this view to:`}</p>
    <ul>
      <li parentName="ul">{`Search and filter payments by account, bank, country, payment type, status, and date range`}</li>
      <li parentName="ul">{`View payment status and history for each transaction`}</li>
      <li parentName="ul">{`Track settlement and understand payment lifecycles`}</li>
      <li parentName="ul">{`Investigate issues with individual payments`}</li>
    </ul>
    <Image src='/assets/img/docs/payment_initiation/payments-dashboard-activity.png' alt='Payments Dashboard Activity tab showing payment list with search filters and status columns' expandable={true} mdxType="Image" />
    <h5 {...{
      "id": "accounts"
    }}>{`Accounts`}</h5>
    <p>{`The Accounts tab provides visibility into your virtual account wallets and associated balances. From this view, you can:`}</p>
    <ul>
      <li parentName="ul">{`Monitor current and available balances for each virtual account`}</li>
      <li parentName="ul">{`View account identifiers including account number and sort code`}</li>
      <li parentName="ul">{`Access the transaction ledger for each account`}</li>
      <li parentName="ul">{`Configure sweep settings and retrieve sweep reports`}</li>
    </ul>
    <Callout mdxType="Callout">
      <p>{`The Accounts tab is only available to customers using the `}<a parentName="p" {...{
          "href": "/docs/payment-initiation/virtual-accounts"
        }}>{`Virtual Accounts`}</a>{` product.`}</p>
    </Callout>
    <h4 {...{
      "id": "payment-details"
    }}>{`Payment details`}</h4>
    <p>{`Select any payment from the Activity tab to view details, including:`}</p>
    <ul>
      <li parentName="ul">{`Current and historical payment status with timestamped payment events`}</li>
      <li parentName="ul">{`Payment amount, currency, and reference information`}</li>
      <li parentName="ul">{`Counterparty details (sender and recipient)`}</li>
    </ul>
    <Image src='/assets/img/docs/payment_initiation/payments-dashboard-payment-detail.png' alt='Payment detail modal showing payment status, transaction details, and activity log' expandable={true} mdxType="Image" />
    <p>{`From the payment detail view, you can raise a support ticket directly if further assistance is required. For more information on payment status transitions and terminal states, see `}<a parentName="p" {...{
        "href": "/docs/payment-initiation/payment-status"
      }}>{`Payment Status`}</a>{`.`}</p>
    <h4 {...{
      "id": "exporting-reports"
    }}>{`Exporting reports`}</h4>
    <p>{`The Payments Dashboard provides CSV export functionality for reconciliation and reporting.`}</p>
    <h5 {...{
      "id": "payment-activity-report"
    }}>{`Payment Activity Report`}</h5>
    <p>{`Export a .csv report containing payment and sweep data from the Activity tab by clicking the "Export" button. You can also view previous exports by selecting "View exports".`}</p>
    <Image src='/assets/img/docs/payment_initiation/payments-dashboard-export.png' alt='Export Payments dialog with filtering options and date range selector' expandable={true} mdxType="Image" />
    <h5 {...{
      "id": "transaction-activity-report"
    }}>{`Transaction Activity Report`}</h5>
    <p>{`For Virtual Accounts customers, the Transactions overview in the Accounts tab allows you to export transaction data for any virtual account as a CSV file. Similar to the Payment Activity Report, you can:`}</p>
    <ul>
      <li parentName="ul">{`Name your export and apply filters`}</li>
      <li parentName="ul">{`Select specific date ranges for transactions`}</li>
      <li parentName="ul">{`Access export history through "View exports"`}</li>
    </ul>
    <h4 {...{
      "id": "virtual-account-management"
    }}>{`Virtual Account management`}</h4>
    <p>{`The Accounts tab displays details for each of your virtual accounts, including:`}</p>
    <ul>
      <li parentName="ul">{`Unique wallet identifier (wallet ID)`}</li>
      <li parentName="ul">{`Current balance and available balance`}</li>
      <li parentName="ul">{`Account number and sort code (for applicable regions)`}</li>
      <li parentName="ul">{`Complete transaction ledger with filtering capabilities`}</li>
    </ul>
    <p>{`Access detailed information about any virtual account by selecting it from the list. For more information on virtual account operations, see `}<a parentName="p" {...{
        "href": "/docs/payment-initiation/virtual-accounts/managing-virtual-accounts"
      }}>{`Managing your Virtual Account`}</a>{`.`}</p>
    <h4 {...{
      "id": "sweep-configuration"
    }}>{`Sweep configuration`}</h4>
    <p>{`For virtual accounts with automated sweeps enabled, the Dashboard provides visibility into sweep operations:`}</p>
    <h5 {...{
      "id": "sweep-history"
    }}>{`Sweep history`}</h5>
    <p>{`View the complete sweep history for your virtual account, including:`}</p>
    <ul>
      <li parentName="ul">{`Date and time of each sweep execution`}</li>
      <li parentName="ul">{`Amount swept from the virtual account`}</li>
      <li parentName="ul">{`Status of each sweep operation`}</li>
    </ul>
    <p>{`You can also download a CSV report detailing all transactions included in a specific sweep.`}</p>
    <h5 {...{
      "id": "sweep-settings"
    }}>{`Sweep settings`}</h5>
    <p>{`Configure and monitor sweep parameters:`}</p>
    <ul>
      <li parentName="ul"><strong parentName="li">{`Minimum balance`}</strong>{`: Set the minimum balance to maintain in the wallet after each sweep. This balance remains in the virtual account and is not included in automated sweeps.`}</li>
      <li parentName="ul"><strong parentName="li">{`Recipient account`}</strong>{`: View the destination account information where funds are swept at the end of each day.`}</li>
    </ul>
    <p>{`For detailed information on sweep functionality and configuration, see `}<a parentName="p" {...{
        "href": "/docs/payment-initiation/virtual-accounts/account-sweeping"
      }}>{`Account Sweeping`}</a>{`.`}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "overview",
        level: 4,
        title: "Overview",
      },
{
        id: "payment-details",
        level: 4,
        title: "Payment details",
      },
{
        id: "exporting-reports",
        level: 4,
        title: "Exporting reports",
      },
{
        id: "virtual-account-management",
        level: 4,
        title: "Virtual Account management",
      },
{
        id: "sweep-configuration",
        level: 4,
        title: "Sweep configuration",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
